import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import VerdictBadge from "@/components/VerdictBadge";
import ImageViewer from "@/components/ImageViewer";
import RegionsList from "@/components/RegionsList";
import ReportTabs from "@/components/ReportTabs";
import ExportPanel from "@/components/ExportPanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useLang } from "@/contexts/AppContexts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const HistoryDetail = () => {
  const { t } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRegionId, setSelectedRegionId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/history/${id}`)
      .then((res) => {
        if (!cancelled) setResult(res.data);
      })
      .catch(() => {
        if (!cancelled) setResult(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="app-shell">
        <Header />
        <main className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-center gap-2 text-muted-foreground py-20">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading…</span>
          </div>
        </main>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="app-shell">
        <Header />
        <main className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-10">
          <Card className="py-12 px-6 text-center">
            <p className="text-muted-foreground">Record not found.</p>
            <Link to="/history" className="inline-block mt-3">
              <Button variant="outline">
                <ChevronLeft className="h-4 w-4 mr-1.5" />
                {t("common.back")}
              </Button>
            </Link>
          </Card>
        </main>
      </div>
    );
  }

  const imageSrc =
    result?.image_b64 &&
    `data:image/${result.image_format?.toLowerCase() || "jpeg"};base64,${result.image_b64}`;
  const elaSrc = result?.ela_b64 && `data:image/png;base64,${result.ela_b64}`;

  return (
    <div className="app-shell">
      <Header />
      <main className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6 lg:py-10 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="history-back-button"
          >
            <ChevronLeft className="h-4 w-4 mr-1.5" />
            {t("common.back")}
          </Button>
          <p className="text-xs text-muted-foreground font-mono-plex truncate">
            {result.filename}
          </p>
        </div>
        <VerdictBadge
          verdict={result.verdict}
          confidence={result.confidence}
          regionsCount={result.gemini?.regions?.length || 0}
          durationMs={result.duration_ms}
          c2paOverride={result.aggregated?.c2pa_override}
          c2paModel={result.aggregated?.c2pa_model}
        />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
          <div className="space-y-4">
            <ImageViewer
              imageSrc={imageSrc}
              elaSrc={elaSrc}
              imageSize={result.metadata?.size}
              regions={result.gemini?.regions || []}
              selectedRegionId={selectedRegionId}
              onRegionClick={(i) => setSelectedRegionId(i)}
            />
            <ReportTabs result={result} />
          </div>
          <div className="space-y-4">
            <RegionsList
              regions={result.gemini?.regions || []}
              selectedId={selectedRegionId}
              onSelect={(i) => setSelectedRegionId(i)}
            />
            <ExportPanel result={result} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default HistoryDetail;
